import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TeamsConnection, TeamsSeat, TeamsStatus, TeamsUpdate } from '@arcnet/contracts';
import { api } from './client.js';

/** Microsoft Teams calling (API modules/teams, docs/44). Every change answers with the whole picture. */

const key = (t: string) => ['teams', t] as const;

export const useTeams = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: key(tenantId), queryFn: () => api.get<TeamsConnection>(`/v1/tenants/${tenantId}/teams`),
    enabled: enabled && !!tenantId,
  });

export function useTeamsMutations(tenantId: string) {
  const qc = useQueryClient();
  const set = (data: TeamsConnection) => qc.setQueryData(key(tenantId), data);
  const base = `/v1/tenants/${tenantId}/teams`;
  return {
    start: useMutation({ mutationFn: (label?: string) => api.post<TeamsConnection>(base, label ? { label } : {}), onSuccess: set }),
    update: useMutation({ mutationFn: (body: TeamsUpdate) => api.patch<TeamsConnection>(base, body), onSuccess: set }),
    remove: useMutation({
      mutationFn: () => api.delete(base),
      onSuccess: () => void qc.invalidateQueries({ queryKey: key(tenantId) }),
    }),
    setSeat: useMutation({
      mutationFn: (v: { extensionId: string; body: TeamsSeat }) => api.put<TeamsConnection>(`${base}/seats/${v.extensionId}`, v.body),
      onSuccess: set,
    }),
    removeSeat: useMutation({
      mutationFn: (extensionId: string) => api.delete<TeamsConnection>(`${base}/seats/${extensionId}`),
      onSuccess: set,
    }),
  };
}

/**
 * Each Teams seat's presence and whether it can take calls (GET /teams/status). Pushed changes
 * re-read it at once (realtime nudge); only while something on screen shows it.
 */
export const useTeamsStatus = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: ['teams', tenantId, 'status'] as const,
    queryFn: () => api.get<TeamsStatus>(`/v1/tenants/${tenantId}/teams/status`),
    enabled,
    // A safety net: changes arrive as realtime nudges within seconds (teamsStatusChanged).
    refetchInterval: 60_000,
    // A platform with Teams switched off answers 409; nothing to retry.
    retry: false,
  });

/** Connecting the account's Microsoft 365: the consent link, then leaving for Microsoft's page. */
export function useTeamsMicrosoft(tenantId: string) {
  const qc = useQueryClient();
  return {
    connect: useMutation({
      mutationFn: () => api.post<{ url: string }>(`/v1/tenants/${tenantId}/teams/microsoft/start`, {}),
      onSuccess: ({ url }) => { window.location.assign(url); },
    }),
    disconnect: useMutation({
      mutationFn: () => api.delete(`/v1/tenants/${tenantId}/teams/microsoft`),
      onSuccess: () => { void qc.invalidateQueries({ queryKey: ['teams', tenantId] }); },
    }),
  };
}
